import React, { useEffect, useMemo } from 'react';
import {
  DateRangeSelector,
  Dropdown,
  DropdownItem,
  DropdownList,
  ENUM_DATE_RANGE,
  Input,
  Paginate
} from '@cokitchen/cokitchen-components';
import { CloseOutlineIcon, FilterAltIcon } from 'cokitchen-icons';
import MultiSelectDropdown, { SelectOption } from 'components/base/multi-select-dropdown';

export type RequestListActiveFilters<T extends string> = {
  status: T[];
  search: string;
  dateRange?: { startDate: Date | null; endDate: Date | null } | null;
};

type StatusMode = 'single' | 'multi';

type FiltersState<StatusType extends string> = {
  status: StatusType[];
  search: string;
  dateRange?: { startDate: Date | null; endDate: Date | null } | null;
  current_page?: number;
};

type Props<F extends FiltersState<string>> = {
  statusOptions: SelectOption<F['status'][number]>[];
  statusAllValue?: F['status'][number];
  statusMode?: StatusMode;
  statusSuffix?: string;
  filters: F;
  onFilterChange: React.Dispatch<React.SetStateAction<F>>;
  searchPlaceholder?: string;
  searchWithinGroup?: boolean;
  pageCount?: number;
  showActiveStatusChips?: boolean;
  onActiveFiltersChange?: (filters: RequestListActiveFilters<F['status'][number]>) => void;
};

const RequestListFilters = <F extends FiltersState<string>>({
  statusOptions,
  statusAllValue,
  statusMode = 'multi',
  statusSuffix = 'requests',
  filters,
  onFilterChange,
  searchPlaceholder = 'Search by code',
  searchWithinGroup = false,
  pageCount,
  showActiveStatusChips = true,
  onActiveFiltersChange
}: Props<F>) => {
  type StatusValue = F['status'][number];
  const { status, search, dateRange } = filters;
  const hasAllValue = statusAllValue !== undefined;

  const normalizeStatuses = (values: StatusValue[]) => Array.from(new Set(values));

  const selectedStatus = useMemo<StatusValue[]>(() => {
    const values = status.length > 0 ? status : hasAllValue ? [statusAllValue as StatusValue] : [];
    return normalizeStatuses(values);
  }, [status, statusAllValue, hasAllValue]);

  const visibleStatusOptions = useMemo(() => {
    if (!hasAllValue) {
      return statusOptions;
    }
    return statusOptions.filter((option) => option.value !== statusAllValue);
  }, [statusOptions, statusAllValue, hasAllValue]);

  const activeStatusFilters = useMemo(() => {
    if (!hasAllValue) {
      return selectedStatus;
    }
    if (selectedStatus.length === 1 && selectedStatus[0] === statusAllValue) {
      return selectedStatus;
    }
    return selectedStatus.filter((value) => value !== statusAllValue);
  }, [selectedStatus, statusAllValue, hasAllValue]);

  useEffect(() => {
    if (!onActiveFiltersChange) return;
    onActiveFiltersChange({
      status: activeStatusFilters,
      search: search || '',
      dateRange: dateRange ?? null
    });
  }, [activeStatusFilters, search, dateRange, onActiveFiltersChange]);

  const statusLabel = useMemo(() => {
    return activeStatusFilters.length === 0
      ? 'Filter by'
      : activeStatusFilters.length === 1
      ? activeStatusFilters[0]
      : `${activeStatusFilters.length} selected`;
  }, [activeStatusFilters]);

  const updateFilters = (next: Partial<F>) => {
    const nextFilters = {
      ...filters,
      ...next
    } as F;
    if (next.status !== undefined) {
      nextFilters.status = normalizeStatuses(next.status as StatusValue[]) as F['status'];
    }
    if (next.status !== undefined || next.search !== undefined || next.dateRange !== undefined) {
      nextFilters.current_page = 1;
    }
    onFilterChange(nextFilters);
  };

  const handleStatusToggle = (value: StatusValue) => {
    if (statusMode === 'single') {
      updateFilters({ status: [value] } as Partial<F>);
      return;
    }

    const current = selectedStatus.length
      ? selectedStatus
      : hasAllValue
      ? [statusAllValue as StatusValue]
      : [];
    if (hasAllValue && value === statusAllValue) {
      updateFilters({ current_page: 1, status: [statusAllValue as StatusValue] } as Partial<F>);
      return;
    }

    const withoutAll = hasAllValue ? current.filter((item) => item !== statusAllValue) : current;

    if (withoutAll.includes(value)) {
      const nextValue = withoutAll.filter((item) => item !== value);
      updateFilters({
        current_page: 1,
        status: nextValue.length ? nextValue : hasAllValue ? [statusAllValue as StatusValue] : []
      } as Partial<F>);
      return;
    }

    updateFilters({ current_page: 1, status: [...withoutAll, value] } as Partial<F>);
  };

  const handleRemoveStatus = (value: StatusValue) => {
    if (statusMode !== 'multi') return;
    const withoutValue = selectedStatus.filter((item) => item !== value && item !== statusAllValue);
    updateFilters({
      current_page: 1,
      status: withoutValue.length
        ? withoutValue
        : hasAllValue
        ? [statusAllValue as StatusValue]
        : []
    } as Partial<F>);
  };

  const renderStatusFilter = () => {
    if (statusMode === 'multi') {
      return (
        <MultiSelectDropdown
          label={statusLabel}
          items={visibleStatusOptions}
          selected={selectedStatus}
          onToggle={handleStatusToggle}
          isActive={activeStatusFilters.length > 0}
        />
      );
    }

    return (
      <Dropdown
        hideArrow
        placement='bottom-start'
        content={
          <div className='border border-[#E5E6E6] rounded-[5px] text-xs leading-[19px] h-[36px] flex px-2 justify-between items-center w-[114px]  cursor-pointer'>
            <span className='text-[#5E6164] text-sm'>Filter by</span>
            <FilterAltIcon
              className={`${activeStatusFilters.length ? 'text-red-5' : 'text-[#5E6164]'}`}
            />
          </div>
        }>
        <DropdownList>
          {visibleStatusOptions.map(({ text, value }, index) => (
            <DropdownItem
              className={`capitalize ${selectedStatus.includes(value) ? 'text-red-5' : ''}`}
              key={index}
              onClick={() => handleStatusToggle(value)}>
              {statusSuffix ? `${text} ${statusSuffix}` : text}
            </DropdownItem>
          ))}
        </DropdownList>
      </Dropdown>
    );
  };

  const searchInput = (
    <Input
      type='search'
      className='w-[280px]'
      inputClass='h-[30px]'
      placeholder={searchPlaceholder}
      value={search ?? ''}
      onChange={(event) =>
        updateFilters({ current_page: 1, search: event.target.value } as Partial<F>)
      }
    />
  );

  const dateInput =
    dateRange !== undefined ? (
      <DateRangeSelector
        defaultRange={ENUM_DATE_RANGE.ALL}
        onChange={(value) =>
          updateFilters({ current_page: 1, dateRange: value.date } as Partial<F>)
        }
        className='text-[#5E6164] h-[36px] text-sm border-[#E5E6E6]'
        isInput
      />
    ) : null;

  const paginate = pageCount ? (
    <Paginate
      pageCount={pageCount}
      pageRangeDisplayed={2}
      onPageChange={(value) => updateFilters({ current_page: value.selected + 1 } as Partial<F>)}
    />
  ) : null;

  return (
    <div className='relative flex items-center gap-4 flex-wrap'>
      <div className='flex gap-3 items-center grow'>
        {renderStatusFilter()}
        {dateInput}
        {searchWithinGroup ? searchInput : null}
      </div>

      {!searchWithinGroup ? searchInput : null}

      {showActiveStatusChips && statusMode === 'multi' && activeStatusFilters.length > 0 && (
        <div className='absolute left-0 top-14 z-10 w-full max-w-[520px] rounded-md p-2 '>
          <ul className='flex flex-wrap gap-2 max-h-[100px] overflow-auto'>
            {activeStatusFilters.map((value) => (
              <li key={value}>
                <button
                  type='button'
                  onClick={() => handleRemoveStatus(value)}
                  className='inline-flex items-center gap-1 rounded-full bg-[#F2F2F3] px-2 py-1 text-xs text-[#2E3031] capitalize hover:bg-[#E5E6E6]'>
                  <span>{value}</span>
                  <CloseOutlineIcon className='text-[#5E6164]' />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {paginate}
    </div>
  );
};

export default RequestListFilters;
